import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  CreditCard,
  Star,
  StarOff,
  FileText,
  Info,
  CheckCircle,
  LayoutGrid,
  Rows,
} from 'lucide-react'
import { CreditCard as CreditCardType } from '@shared/firebase/schema'
import toast from 'react-hot-toast'
import { useLocalization } from '../contexts/LocalizationContext'
import {
  useCreditCards,
  useDeleteCard,
  useToggleFeatured,
} from '../lib/queries/cards'
import {
  Badge,
  Button,
  buttonStyles,
  Card,
  DataTable,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  cn,
  useConfirm,
} from '../components/ui'

type MitcFilter = 'all' | 'with-mitc' | 'without-mitc'
type ViewMode = 'table' | 'grid'
type CardSort = 'name' | 'bank' | 'category'
const PAGE_SIZE = 20

export default function CreditCards() {
  const { t } = useLocalization()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedBank, setSelectedBank] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [mitcFilter, setMitcFilter] = useState<MitcFilter>('all')
  const [viewMode, setViewMode] = useState<ViewMode>('table')
  const [sort, setSort] = useState<CardSort>('name')
  const [pageNumber, setPageNumber] = useState(1)
  const [selectedCards, setSelectedCards] = useState<string[]>([])

  const { data: cards = [], isLoading, isError, error } = useCreditCards({})
  const deleteMutation = useDeleteCard()
  const toggleFeatured = useToggleFeatured()
  const confirm = useConfirm()

  const handleDelete = async (cardId: string, cardName: string) => {
    const ok = await confirm({
      title: 'Delete this credit card?',
      body: <>This will permanently remove <strong>{cardName}</strong> from the database. This can't be undone.</>,
      confirmLabel: 'Delete card',
      destructive: true,
    })
    if (!ok) return
    deleteMutation.mutate(cardId, {
      onSuccess: () => toast.success(t('SUCCESS.DELETED', { item: 'Card' })),
      onError:   () => toast.error(t('ERRORS.DELETE_FAILED')),
    })
  }

  const handleToggleFeatured = (cardId: string, isFeatured: boolean) => {
    toggleFeatured.mutate({ cardId, isFeatured: !isFeatured }, {
      onSuccess: () => toast.success(t('SUCCESS.UPDATED', { item: 'Card' })),
      onError:   () => toast.error(t('ERRORS.UPDATE_FAILED')),
    })
  }

  // Filter and sort the complete bounded merged directory before pagination.
  const filteredCards = cards.filter(card => {
    const matchesSearch =
      card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      card.bank.toLowerCase().includes(searchTerm.toLowerCase()) ||
      card.category.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesMitc =
      mitcFilter === 'all' ||
      (mitcFilter === 'with-mitc'    &&  card.mitc) ||
      (mitcFilter === 'without-mitc' && !card.mitc)
    return matchesSearch && matchesMitc &&
      (!selectedBank || card.bank === selectedBank) &&
      (!selectedCategory || card.category === selectedCategory)
  }).sort((a, b) =>
    String(a[sort] || '').localeCompare(String(b[sort] || '')) || a.id.localeCompare(b.id))
  const pageCount = Math.max(1, Math.ceil(filteredCards.length / PAGE_SIZE))
  const currentPage = Math.min(pageNumber, pageCount)
  const visibleCards = filteredCards.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  const banks      = [...new Set(cards.map(c => c.bank))].sort()
  const categories = [...new Set(cards.map(c => c.category))].sort()
  const mitcStats = {
    total:      cards.length,
    withMitc:   cards.filter(c =>  c.mitc).length,
    withoutMitc:cards.filter(c => !c.mitc).length,
  }
  const coverage = cards.length === 0 ? 0 : Math.round((mitcStats.withMitc / cards.length) * 100)

  const handleSelectAll = () => {
    setSelectedCards(prev => visibleCards.every(card => prev.includes(card.id))
      ? prev.filter(id => !visibleCards.some(card => card.id === id))
      : [...new Set([...prev, ...visibleCards.map(card => card.id)])])
  }

  const handleBulkFeatured = (isFeatured: boolean) => {
    selectedCards.forEach(cardId => toggleFeatured.mutate({ cardId, isFeatured }))
    setSelectedCards([])
  }

  const handleBulkDelete = async () => {
    const n = selectedCards.length
    const ok = await confirm({
      title: `Delete ${n} selected card${n === 1 ? '' : 's'}?`,
      body: `This will permanently remove ${n === 1 ? 'this card' : 'these cards'} from the database. This can't be undone.`,
      confirmLabel: `Delete ${n}`,
      destructive: true,
    })
    if (!ok) return
    selectedCards.forEach(cardId => deleteMutation.mutate(cardId))
    setSelectedCards([])
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedBank('')
    setSelectedCategory('')
    setMitcFilter('all')
    setPageNumber(1)
    setSelectedCards([])
  }

  const anyFilter = searchTerm || selectedBank || selectedCategory || mitcFilter !== 'all'

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <PageHeader
        title={t('CREDIT_CARDS.TITLE')}
        subtitle="Merged credit card and MITC-derived records. Filters and counts cover this bounded directory; MITC adapter fields need source verification."
        actions={
          <Link to="/cards/new" className={buttonStyles({ variant: 'primary', size: 'md' })}>
            <Plus size={16} className="mr-2 -ml-0.5" />
            {t('CREDIT_CARDS.ADD_NEW')}
          </Link>
        }
      />

      {/* MITC Coverage stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <p className="text-xs uppercase tracking-wider font-medium text-ink-muted">
            {t('CREDIT_CARDS.TOTAL_CARDS')}
          </p>
          <p className="mt-1 font-display text-3xl text-ink tabular-nums">{isError ? 'Unavailable' : isLoading ? '…' : mitcStats.total}</p>
        </Card>
        <Card>
          <p className="text-xs uppercase tracking-wider font-medium text-ink-muted">
            {t('CREDIT_CARDS.WITH_MITC')}
          </p>
          <p className="mt-1 font-display text-3xl text-success tabular-nums">{isError ? 'Unavailable' : isLoading ? '…' : mitcStats.withMitc}</p>
        </Card>
        <Card>
          <p className="text-xs uppercase tracking-wider font-medium text-ink-muted">
            {t('CREDIT_CARDS.WITHOUT_MITC')}
          </p>
          <p className="mt-1 font-display text-3xl text-warning tabular-nums">{isError ? 'Unavailable' : isLoading ? '…' : mitcStats.withoutMitc}</p>
        </Card>
        <Card accent>
          <p className="text-xs uppercase tracking-wider font-medium text-ink-muted">Coverage</p>
          <p className="mt-1 font-display text-3xl text-accent-deep dark:text-accent-bright tabular-nums">
            {isError ? 'Unavailable' : isLoading ? '…' : <>{coverage}<span className="text-xl text-ink-muted">%</span></>}
          </p>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <Input
              type="search"
              placeholder={t('CREDIT_CARDS.SEARCH_PLACEHOLDER')}
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setPageNumber(1); setSelectedCards([]) }}
              leftIcon={<Search size={16} />}
            />
          </div>
          <Select value={selectedBank} onChange={e => { setSelectedBank(e.target.value); setPageNumber(1); setSelectedCards([]) }} aria-label="Filter cards by bank">
            <option value="">{t('CREDIT_CARDS.ALL_BANKS')}</option>
            {banks.map(bank => <option key={bank} value={bank}>{bank}</option>)}
          </Select>
          <Select value={selectedCategory} onChange={e => { setSelectedCategory(e.target.value); setPageNumber(1); setSelectedCards([]) }} aria-label="Filter cards by category">
            <option value="">{t('CREDIT_CARDS.ALL_CATEGORIES')}</option>
            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </Select>
          <Select value={mitcFilter} onChange={e => { setMitcFilter(e.target.value as MitcFilter); setPageNumber(1); setSelectedCards([]) }} aria-label="Filter cards by MITC availability">
            <option value="all">{t('CREDIT_CARDS.ALL_MITC_STATUS')}</option>
            <option value="with-mitc">{t('CREDIT_CARDS.WITH_MITC')}</option>
            <option value="without-mitc">{t('CREDIT_CARDS.WITHOUT_MITC')}</option>
          </Select>
          <Select value={sort} onChange={e => { setSort(e.target.value as CardSort); setPageNumber(1); setSelectedCards([]) }} aria-label="Sort cards">
            <option value="name">Card name: A to Z</option>
            <option value="bank">Bank: A to Z</option>
            <option value="category">Category: A to Z</option>
          </Select>
        </div>
        {anyFilter && (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-ink-muted">
              Showing <span className="font-medium text-ink tabular-nums">{filteredCards.length}</span> of{' '}
              <span className="tabular-nums">{cards.length}</span> cards
            </span>
            <Button variant="ghost" size="sm" leftIcon={<Filter size={14} />} onClick={clearFilters}>
              {t('ACTIONS.CLEAR_FILTERS')}
            </Button>
          </div>
        )}
      </Card>

      {/* View toggle + bulk actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div role="tablist" aria-label="View mode" className="inline-flex rounded-xl bg-surface-elevated p-1 border border-line">
          <button
            role="tab"
            aria-selected={viewMode === 'table'}
            onClick={() => setViewMode('table')}
            className={cn(
              'h-8 px-3 inline-flex items-center gap-1.5 rounded-lg text-sm font-medium transition-colors',
              viewMode === 'table' ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted hover:text-ink',
            )}
          >
            <Rows size={14} />
            Table
          </button>
          <button
            role="tab"
            aria-selected={viewMode === 'grid'}
            onClick={() => setViewMode('grid')}
            className={cn(
              'h-8 px-3 inline-flex items-center gap-1.5 rounded-lg text-sm font-medium transition-colors',
              viewMode === 'grid' ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted hover:text-ink',
            )}
          >
            <LayoutGrid size={14} />
            Grid
          </button>
        </div>

        {selectedCards.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-ink-muted">
              <span className="tabular-nums">{selectedCards.length}</span> selected
            </span>
            <Button variant="secondary" size="sm" leftIcon={<Star size={14} />} onClick={() => handleBulkFeatured(true)}>
              Mark featured
            </Button>
            <Button variant="secondary" size="sm" leftIcon={<StarOff size={14} />} onClick={() => handleBulkFeatured(false)}>
              Unmark
            </Button>
            <Button variant="destructive" size="sm" leftIcon={<Trash2 size={14} />} onClick={handleBulkDelete}>
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <Card unpadded>
          <div className="p-6 space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
          </div>
        </Card>
      ) : isError ? (
        <Card><EmptyState icon={<CreditCard size={24} />} title="Couldn't load card directory" description={error instanceof Error ? error.message : 'The directory query failed.'} /></Card>
      ) : filteredCards.length === 0 ? (
        <Card>
          <EmptyState
            icon={<CreditCard size={24} />}
            title={t('CREDIT_CARDS.NO_CARDS_FOUND')}
            description={anyFilter ? t('CREDIT_CARDS.ADJUST_FILTERS') : t('CREDIT_CARDS.GET_STARTED')}
            action={
              !anyFilter ? (
                <Link to="/cards/new" className={buttonStyles({ variant: 'primary', size: 'md' })}>
                  <Plus size={16} className="mr-2 -ml-0.5" />
                  {t('CREDIT_CARDS.ADD_NEW')}
                </Link>
              ) : (
                <Button variant="secondary" size="md" onClick={clearFilters}>
                  Clear filters
                </Button>
              )
            }
          />
        </Card>
      ) : viewMode === 'table' ? (
        <DataTable>
          <THead>
            <TR>
              <TH className="w-12">
                <input
                  type="checkbox"
                  checked={visibleCards.length > 0 && visibleCards.every(card => selectedCards.includes(card.id))}
                  onChange={handleSelectAll}
                  aria-label="Select all cards on this page"
                  className="h-4 w-4 rounded border-line accent-brand"
                />
              </TH>
              <TH>Card</TH>
              <TH>Bank</TH>
              <TH>Category</TH>
              <TH className="text-right">Annual Fee</TH>
              <TH>Cashback</TH>
              <TH>MITC</TH>
              <TH className="text-right">Actions</TH>
            </TR>
          </THead>
          <TBody>
            {visibleCards.map(card => (
              <CardRow
                key={card.id}
                card={card}
                isSelected={selectedCards.includes(card.id)}
                onSelect={(id, sel) =>
                  setSelectedCards(prev => (sel ? [...prev, id] : prev.filter(x => x !== id)))
                }
                onDelete={handleDelete}
                onToggleFeatured={handleToggleFeatured}
              />
            ))}
          </TBody>
        </DataTable>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleCards.map(card => (
            <GridCard
              key={card.id}
              card={card}
              onDelete={handleDelete}
              onToggleFeatured={handleToggleFeatured}
            />
          ))}
        </div>
      )}
      {!isLoading && !isError && filteredCards.length > 0 && (
        <div className="flex items-center justify-between gap-3 text-sm text-ink-muted" aria-label="Card pages">
          <span>Page {currentPage} of {pageCount} · {filteredCards.length} matching cards</span>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" disabled={currentPage <= 1} onClick={() => { setPageNumber(currentPage - 1); setSelectedCards([]) }}>Previous</Button>
            <Button variant="secondary" size="sm" disabled={currentPage >= pageCount} onClick={() => { setPageNumber(currentPage + 1); setSelectedCards([]) }}>Next</Button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Row in table view ───────────────────────────────────────────────
function CardRow({
  card,
  isSelected,
  onSelect,
  onDelete,
  onToggleFeatured,
}: {
  card: CreditCardType
  isSelected: boolean
  onSelect: (cardId: string, selected: boolean) => void
  onDelete: (cardId: string, cardName: string) => void
  onToggleFeatured: (cardId: string, isFeatured: boolean) => void
}) {
  const { formatCurrency } = useLocalization()

  return (
    <TR>
      <TD>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={e => onSelect(card.id, e.target.checked)}
          className="h-4 w-4 rounded border-line accent-brand"
        />
      </TD>
      <TD>
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-surface-elevated overflow-hidden flex-shrink-0">
            {card.imageUrl ? (
              <img
                src={card.imageUrl}
                alt={card.name}
                className="object-cover w-8 h-8 rounded-lg"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
              />
            ) : (
              <CreditCard className="h-4 w-4 text-ink-subtle" />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-ink truncate max-w-[12rem]">{card.name}</span>
              {card.isFeatured && <Star className="h-3 w-3 text-accent fill-current flex-shrink-0" />}
              {card.createdBy === 'mitc-import' && <Badge tone="brand">MITC</Badge>}
            </div>
          </div>
        </div>
      </TD>
      <TD className="text-ink">{card.bank}</TD>
      <TD>
        <Badge tone="info">{card.category}</Badge>
      </TD>
      <TD className="text-right tabular-nums">
        {card.mitc?.annualFee || formatCurrency(card.fees?.annualFee || card.annualFee || 0)}
      </TD>
      <TD className="text-ink tabular-nums">
        {card.mitc?.cashbackRate || `${card.rewards?.cashbackRate ?? 0}%`}
      </TD>
      <TD>
        {card.mitc ? (
          <Badge tone="success">
            <CheckCircle size={11} /> Available
          </Badge>
        ) : (
          <Badge tone="neutral">
            <Info size={11} /> Pending
          </Badge>
        )}
      </TD>
      <TD>
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => onToggleFeatured(card.id, card.isFeatured)}
            className={cn(
              'h-8 w-8 inline-flex items-center justify-center rounded-lg transition-colors',
              card.isFeatured
                ? 'text-accent hover:bg-accent/10'
                : 'text-ink-subtle hover:text-ink hover:bg-surface-elevated',
            )}
            title={card.isFeatured ? 'Remove featured' : 'Mark featured'}
          >
            {card.isFeatured ? <Star size={14} className="fill-current" /> : <StarOff size={14} />}
          </button>

          {card.createdBy === 'mitc-import' && (
            <button
              onClick={() => onDelete(card.id, card.name)}
              className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-error hover:bg-error/10 transition-colors"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          )}

          <Link
            to={`/cards/${card.id}/edit`}
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-brand hover:bg-brand/10 transition-colors"
            title="Edit"
          >
            <Edit size={14} />
          </Link>
          <Link
            to={`/cards/${card.id}`}
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-ink-muted hover:text-ink hover:bg-surface-elevated transition-colors"
            title="View"
          >
            <Eye size={14} />
          </Link>
        </div>
      </TD>
    </TR>
  )
}

// ── Card tile in grid view ─────────────────────────────────────────
function GridCard({
  card,
  onDelete,
  onToggleFeatured,
}: {
  card: CreditCardType
  onDelete: (cardId: string, cardName: string) => void
  onToggleFeatured: (cardId: string, isFeatured: boolean) => void
}) {
  const { t, formatCurrency } = useLocalization()

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-12 w-12 rounded-xl bg-surface-elevated overflow-hidden flex items-center justify-center flex-shrink-0">
            {card.imageUrl ? (
              <img
                src={card.imageUrl}
                alt={card.name}
                className="object-cover w-12 h-12 rounded-xl"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
              />
            ) : (
              <CreditCard className="h-5 w-5 text-ink-subtle" />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="font-display text-lg text-ink truncate">{card.name}</h3>
              {card.isFeatured && <Star className="h-4 w-4 text-accent fill-current flex-shrink-0" />}
            </div>
            <p className="text-sm text-ink-muted">{card.bank}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {card.createdBy === 'mitc-import' && <Badge tone="brand">MITC</Badge>}
          <button
            onClick={() => onToggleFeatured(card.id, card.isFeatured)}
            className={cn(
              'h-8 w-8 inline-flex items-center justify-center rounded-lg transition-colors',
              card.isFeatured
                ? 'text-accent hover:bg-accent/10'
                : 'text-ink-subtle hover:text-ink hover:bg-surface-elevated',
            )}
            title={card.isFeatured ? t('ACTIONS.REMOVE_FEATURED') : t('ACTIONS.MARK_FEATURED')}
          >
            {card.isFeatured ? <Star size={16} className="fill-current" /> : <StarOff size={16} />}
          </button>
        </div>
      </div>

      {/* MITC indicator */}
      {card.mitc ? (
        <div className="rounded-xl border border-success/20 bg-success/5 px-3 py-2.5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-success inline-flex items-center gap-1">
              <FileText size={12} /> MITC data available
            </span>
            <Badge tone="success">
              <CheckCircle size={11} /> Stored MITC
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-1.5 text-xs">
            {card.mitc.annualFee && <Row label="Annual" value={card.mitc.annualFee} />}
            {card.mitc.joiningFee && <Row label="Joining" value={card.mitc.joiningFee} />}
            {card.mitc.interestRate && <Row label="Interest" value={card.mitc.interestRate} />}
            {card.mitc.cashbackRate && <Row label="Cashback" value={card.mitc.cashbackRate} />}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-line bg-surface-elevated/50 px-3 py-2.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-ink-muted inline-flex items-center gap-1">
              <Info size={12} /> MITC data pending
            </span>
            <Badge tone="neutral">Awaiting</Badge>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-ink-muted">{t('CREDIT_CARDS.ANNUAL_FEE')}</span>
          <span className="font-medium text-ink tabular-nums">
            {card.mitc?.annualFee || formatCurrency(card.fees?.annualFee || card.annualFee || 0)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-ink-muted">{t('CREDIT_CARDS.CASHBACK_RATE')}</span>
          <span className="font-medium text-ink tabular-nums">
            {card.mitc?.cashbackRate || `${card.rewards?.cashbackRate ?? 0}%`}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-ink-muted">{t('CREDIT_CARDS.CATEGORY')}</span>
          <Badge tone="info">{card.category}</Badge>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-auto pt-3 border-t border-line flex items-center justify-between">
        <p className="text-xs text-ink-subtle line-clamp-1 flex-1 mr-3">{card.description}</p>
        <div className="flex items-center gap-1 flex-shrink-0">
          {card.createdBy === 'mitc-import' && (
            <button
              onClick={() => onDelete(card.id, card.name)}
              className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-error hover:bg-error/10 transition-colors"
              title={t('ACTIONS.DELETE')}
            >
              <Trash2 size={14} />
            </button>
          )}
          <Link
            to={`/cards/${card.id}/edit`}
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-brand hover:bg-brand/10 transition-colors"
            title={t('ACTIONS.EDIT')}
          >
            <Edit size={14} />
          </Link>
        </div>
      </div>
    </Card>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2 min-w-0">
      <span className="text-ink-subtle">{label}:</span>
      <span className="font-medium text-ink truncate tabular-nums">{value}</span>
    </div>
  )
}
